import { useSignal } from "@preact/signals";
import {
  IcoCalendar,
  IcoCheck,
  IcoChevronLeft,
  IcoChevronRight,
  IcoEye,
  IcoFileText,
  IcoLogo,
  IcoPlus,
  IcoStar,
  IcoText,
  IcoUsers,
} from "../components/icons";
import { RatingInput } from "../components/ui";
import { usePageTitle } from "../hooks";

// Demo data
const DEMO_ORG = { name: "Acme Inc", logoUrl: null };
const DEMO_FORM = {
  title: "Weekly Team Check-in",
  questions: [
    {
      id: "q1",
      title: "How are you feeling about your workload this week?",
      description: "Be honest - this helps us balance the team better.",
      type: "rating" as const,
      ratingMax: 5,
    },
    {
      id: "q2",
      title: "What's one thing that went well?",
      description: "",
      type: "short_answer" as const,
      ratingMax: 5,
    },
    {
      id: "q3",
      title: "Any blockers or concerns?",
      description: "We'll address these in our next standup.",
      type: "short_answer" as const,
      ratingMax: 5,
    },
  ],
};

const DEMO_RESULTS = {
  submissionCount: 8,
  questions: [
    {
      id: "q1",
      title: "How are you feeling about your workload this week?",
      type: "rating" as const,
      ratingMax: 5,
      avgRating: 3.8,
      responseCount: 8,
    },
    {
      id: "q2",
      title: "What's one thing that went well?",
      type: "short_answer" as const,
      ratingMax: 5,
      responses: [
        {
          name: "Sarah Chen",
          text: "Shipped the new dashboard feature ahead of schedule!",
        },
        {
          name: "Mike Johnson",
          text: "Great collaboration with the design team on the rebrand.",
        },
        { name: "Alex Rivera", text: "Finally fixed that tricky caching bug." },
      ],
    },
  ],
};

type Step = {
  id: string;
  title: string;
  description: string;
  highlights?: string[];
};

const STEPS: Step[] = [
  {
    id: "welcome",
    title: "Welcome to Pulse",
    description:
      "Pulse helps teams stay connected through lightweight, recurring check-ins. Let's walk through how it works.",
  },
  {
    id: "create-form",
    title: "Create a Form",
    description:
      "Admins create forms with questions for their team. Forms can be scheduled to recur weekly, biweekly, or monthly.",
    highlights: ["Edit title directly", "Set schedule", "Assign team members"],
  },
  {
    id: "add-questions",
    title: "Add Questions",
    description:
      "Add rating scales or open-ended questions. Each question can have a title and optional description.",
    highlights: [
      "Rating questions (1-5, 1-10, etc.)",
      "Short answer for open feedback",
      "Reorder with drag & drop",
    ],
  },
  {
    id: "preview",
    title: "Preview Your Form",
    description:
      "Use the Preview button to see exactly what team members will see when filling out the form.",
    highlights: [
      "Live preview updates as you edit",
      "See the end-user experience",
    ],
  },
  {
    id: "team-view",
    title: "Team Member Dashboard",
    description:
      "Team members see their pending forms on their dashboard. They get notified when new forms are ready.",
    highlights: ["Clear due dates", "Easy access to pending forms"],
  },
  {
    id: "submit-form",
    title: "Fill Out a Form",
    description:
      "Team members answer questions with a clean, focused interface. Responses auto-save as drafts.",
    highlights: [
      "Simple rating buttons",
      "Rich text for detailed answers",
      "Submit when ready",
    ],
  },
  {
    id: "view-results",
    title: "Review Results",
    description:
      "Admins see aggregated results with average ratings and all text responses. Filter by time period.",
    highlights: [
      "Average ratings at a glance",
      "All responses in one place",
      "Historical data by period",
    ],
  },
];

export function Page() {
  usePageTitle("Demo");
  const currentStep = useSignal(0);
  const step = STEPS[currentStep.value];

  const goNext = () => {
    if (currentStep.value < STEPS.length - 1) {
      currentStep.value++;
    }
  };

  const goPrev = () => {
    if (currentStep.value > 0) {
      currentStep.value--;
    }
  };

  const goToStep = (index: number) => {
    currentStep.value = index;
  };

  return (
    <div class="min-h-screen bg-surface-page">
      {/* Top bar */}
      <div class="bg-surface border-b border-border">
        <div class="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div class="flex items-center gap-3">
            <IcoLogo class="size-8" />
            <span class="font-semibold text-text">Pulse Demo</span>
          </div>
          <a
            href="/login"
            class="text-sm font-medium text-text-secondary hover:text-text transition-colors"
          >
            Sign in to get started →
          </a>
        </div>
      </div>

      <div class="max-w-5xl mx-auto px-4 py-8">
        {/* Progress dots */}
        <div class="flex justify-center gap-2 mb-8">
          {STEPS.map((s, i) => (
            <button
              key={s.id}
              type="button"
              onClick={() => goToStep(i)}
              class={`size-2.5 rounded-full transition-colors ${
                i === currentStep.value
                  ? "bg-text"
                  : i < currentStep.value
                    ? "bg-text-muted"
                    : "bg-border"
              }`}
              aria-label={`Go to step ${i + 1}: ${s.title}`}
            />
          ))}
        </div>

        {/* Main content area */}
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Demo screen */}
          <div class="lg:col-span-2">
            <div class="bg-surface rounded-xl border border-border shadow-lg overflow-hidden">
              <DemoScreen stepId={step?.id ?? "welcome"} />
            </div>
          </div>

          {/* Tour explanation */}
          <div class="lg:col-span-1">
            <div class="bg-surface rounded-xl border border-border p-6 sticky top-8">
              <div class="flex items-center gap-2 text-sm text-text-muted mb-2">
                Step {currentStep.value + 1} of {STEPS.length}
              </div>
              <h2 class="text-xl font-bold text-text mb-3">{step?.title}</h2>
              <p class="text-text-secondary mb-4">{step?.description}</p>

              {step?.highlights && step.highlights.length > 0 && (
                <div class="mb-6">
                  <p class="text-sm font-medium text-text-muted mb-2">
                    Key features:
                  </p>
                  <ul class="space-y-1.5">
                    {step.highlights.map((h) => (
                      <li
                        key={h}
                        class="flex items-start gap-2 text-sm text-text-secondary"
                      >
                        <IcoCheck class="size-4 text-green-500 mt-0.5 flex-shrink-0" />
                        {h}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Navigation */}
              <div class="flex gap-3">
                <button
                  type="button"
                  onClick={goPrev}
                  disabled={currentStep.value === 0}
                  class="flex-1 flex items-center justify-center gap-1 px-4 py-2 rounded-lg border border-border text-sm font-medium text-text-secondary hover:bg-surface-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <IcoChevronLeft class="size-4" />
                  Back
                </button>
                {currentStep.value < STEPS.length - 1 ? (
                  <button
                    type="button"
                    onClick={goNext}
                    class="flex-1 flex items-center justify-center gap-1 px-4 py-2 rounded-lg bg-text text-surface text-sm font-medium hover:opacity-90 transition-colors"
                  >
                    Next
                    <IcoChevronRight class="size-4" />
                  </button>
                ) : (
                  <a
                    href="/signup"
                    class="flex-1 flex items-center justify-center gap-1 px-4 py-2 rounded-lg bg-text text-surface text-sm font-medium hover:opacity-90 transition-colors"
                  >
                    Get Started
                    <IcoChevronRight class="size-4" />
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Demo Screens
// ============================================================================

function DemoScreen({ stepId }: { stepId: string }) {
  switch (stepId) {
    case "welcome":
      return <WelcomeScreen />;
    case "create-form":
      return <CreateFormScreen />;
    case "add-questions":
      return <AddQuestionsScreen />;
    case "preview":
      return <PreviewScreen />;
    case "team-view":
      return <TeamDashboardScreen />;
    case "submit-form":
      return <SubmitFormScreen />;
    case "view-results":
      return <ResultsScreen />;
    default:
      return <WelcomeScreen />;
  }
}

function WelcomeScreen() {
  return (
    <div class="p-12 text-center">
      <IcoLogo class="size-16 mx-auto mb-6" />
      <h1 class="text-3xl font-bold text-text mb-4">
        Team check-ins made simple
      </h1>
      <p class="text-lg text-text-secondary max-w-md mx-auto mb-8">
        Create recurring forms, gather feedback, and keep your team aligned —
        all in one place.
      </p>
      <div class="flex justify-center gap-8 text-left">
        <Feature
          icon={<IcoFileText class="size-6" />}
          title="Easy Forms"
          description="Build forms in minutes"
        />
        <Feature
          icon={<IcoCalendar class="size-6" />}
          title="Auto Schedule"
          description="Weekly, biweekly, monthly"
        />
        <Feature
          icon={<IcoUsers class="size-6" />}
          title="Team Insights"
          description="See trends over time"
        />
      </div>
    </div>
  );
}

function Feature({
  icon,
  title,
  description,
}: {
  icon: preact.JSX.Element;
  title: string;
  description: string;
}) {
  return (
    <div class="flex flex-col items-center gap-2">
      <div class="size-12 rounded-lg bg-surface-alt flex items-center justify-center text-text-muted">
        {icon}
      </div>
      <p class="font-medium text-text">{title}</p>
      <p class="text-sm text-text-muted">{description}</p>
    </div>
  );
}

function CreateFormScreen() {
  return (
    <div class="p-6">
      {/* Simulated form editor header */}
      <div class="flex items-center gap-2 text-sm text-text-muted mb-4">
        <IcoChevronLeft class="size-4" />
        <span>Back to Forms</span>
      </div>

      <div class="mb-6">
        <h1 class="text-2xl font-bold text-text mb-1">{DEMO_FORM.title}</h1>
        <div class="flex items-center gap-3 text-sm">
          <span class="text-text-muted">{DEMO_ORG.name}</span>
          <span class="text-text-subtle">·</span>
          <span class="text-text-secondary flex items-center gap-1">
            <IcoEye class="size-4" /> Preview
          </span>
        </div>
      </div>

      {/* Schedule & Assignment chips */}
      <div class="flex gap-3 mb-6">
        <div class="flex-1 flex items-center gap-3 px-4 py-3 rounded-xl border border-border bg-surface-alt">
          <IcoCalendar class="size-5 text-text-muted" />
          <span class="text-sm font-medium text-text-secondary">
            Weekly on Fridays
          </span>
          <IcoChevronRight class="size-4 text-text-subtle ml-auto" />
        </div>
        <div class="flex-1 flex items-center gap-3 px-4 py-3 rounded-xl border border-border bg-surface-alt">
          <IcoUsers class="size-5 text-text-muted" />
          <span class="text-sm font-medium text-text-secondary">12 people</span>
          <IcoChevronRight class="size-4 text-text-subtle ml-auto" />
        </div>
      </div>

      <div class="flex items-center justify-between mb-4">
        <h2 class="text-lg font-semibold text-text">Questions (3)</h2>
      </div>

      {/* Question preview */}
      <div class="space-y-3 opacity-60">
        {DEMO_FORM.questions.map((q, i) => (
          <div
            key={q.id}
            class="p-4 rounded-xl border border-border bg-surface"
          >
            <div class="flex items-start gap-3">
              <span class="size-6 rounded-full bg-surface-muted flex items-center justify-center text-xs font-medium text-text-muted">
                {i + 1}
              </span>
              <div class="flex-1">
                <p class="font-medium text-text">{q.title}</p>
                {q.description && (
                  <p class="text-sm text-text-muted mt-1">{q.description}</p>
                )}
              </div>
              <div class="text-xs px-2 py-1 rounded bg-surface-alt text-text-muted">
                {q.type === "rating" ? `1-${q.ratingMax}` : "Text"}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AddQuestionsScreen() {
  return (
    <div class="p-6">
      <div class="flex items-center justify-between mb-4">
        <h2 class="text-lg font-semibold text-text">Questions (3)</h2>
        <button
          type="button"
          class="text-sm font-medium text-text-muted flex items-center gap-1"
        >
          Reorder
        </button>
      </div>

      {/* Expanded question editor */}
      <div class="space-y-3">
        <div class="p-4 rounded-xl border-2 border-text bg-surface">
          <div class="flex items-start gap-3 mb-4">
            <span class="size-6 rounded-full bg-text text-surface flex items-center justify-center text-xs font-medium">
              1
            </span>
            <div class="flex-1">
              <input
                type="text"
                value={DEMO_FORM.questions[0]?.title}
                class="w-full font-medium text-text bg-transparent border-0 p-0 focus:outline-none"
                readOnly
              />
              <input
                type="text"
                value={DEMO_FORM.questions[0]?.description}
                placeholder="Add description (optional)"
                class="w-full text-sm text-text-muted bg-transparent border-0 p-0 mt-1 focus:outline-none"
                readOnly
              />
            </div>
          </div>
          <div class="flex items-center gap-4 pl-9">
            <label class="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="type"
                checked
                class="accent-text"
                readOnly
              />
              <span>Rating (1-5)</span>
            </label>
            <label class="flex items-center gap-2 text-sm text-text-muted">
              <input type="radio" name="type" class="accent-text" readOnly />
              <span>Short answer</span>
            </label>
          </div>
        </div>

        {DEMO_FORM.questions.slice(1).map((q, i) => (
          <div
            key={q.id}
            class="p-4 rounded-xl border border-border bg-surface opacity-60"
          >
            <div class="flex items-start gap-3">
              <span class="size-6 rounded-full bg-surface-muted flex items-center justify-center text-xs font-medium text-text-muted">
                {i + 2}
              </span>
              <div class="flex-1">
                <p class="font-medium text-text">{q.title}</p>
              </div>
              <div class="text-xs px-2 py-1 rounded bg-surface-alt text-text-muted">
                {q.type === "rating" ? `1-${q.ratingMax}` : "Text"}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Add question button */}
      <button
        type="button"
        class="mt-4 w-full flex items-center justify-center gap-2 p-4 rounded-xl border-2 border-dashed border-border text-text-muted"
      >
        <IcoPlus class="size-5" />
        <span class="font-medium">Add question</span>
      </button>
    </div>
  );
}

function PreviewScreen() {
  return (
    <div class="flex">
      {/* Editor side (compressed) */}
      <div class="w-1/2 p-4 border-r border-border opacity-50">
        <div class="text-sm text-text-muted mb-4">Form Editor</div>
        <div class="space-y-2">
          {DEMO_FORM.questions.map((q, i) => (
            <div key={q.id} class="p-2 rounded border border-border text-xs">
              <span class="text-text-muted">{i + 1}.</span>{" "}
              {q.title.slice(0, 30)}...
            </div>
          ))}
        </div>
      </div>

      {/* Preview side */}
      <div class="w-1/2 p-4 bg-surface-alt">
        <div class="flex items-center gap-2 text-sm font-medium text-text mb-4">
          <IcoEye class="size-4" />
          Form Preview
        </div>

        <div class="bg-surface rounded-lg p-4 shadow-sm">
          <div class="flex items-start gap-3 mb-4">
            <div class="size-10 rounded-lg bg-surface-alt flex items-center justify-center">
              <IcoFileText class="size-5 text-text-muted" />
            </div>
            <div>
              <h3 class="font-bold text-text">{DEMO_FORM.title}</h3>
              <p class="text-xs text-text-muted">{DEMO_ORG.name}</p>
            </div>
          </div>

          <div class="space-y-3">
            {DEMO_FORM.questions.slice(0, 2).map((q, i) => (
              <div key={q.id} class="p-3 rounded-lg border border-border">
                <div class="flex items-start gap-2 mb-2">
                  <span class="size-5 rounded-full bg-surface-muted flex items-center justify-center text-xs text-text-muted">
                    {i + 1}
                  </span>
                  <p class="text-sm font-medium text-text">{q.title}</p>
                </div>
                {q.type === "rating" ? (
                  <div class="flex gap-1 pl-7">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <div
                        key={n}
                        class="size-6 rounded border border-border text-xs flex items-center justify-center text-text-muted"
                      >
                        {n}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div class="ml-7 h-8 rounded border border-dashed border-border bg-surface-muted" />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function TeamDashboardScreen() {
  return (
    <div class="p-6">
      <div class="flex items-center justify-between mb-6">
        <div>
          <h1 class="text-xl font-bold text-text">Welcome back, Sarah</h1>
          <p class="text-sm text-text-muted">{DEMO_ORG.name}</p>
        </div>
        <div class="size-10 rounded-full bg-blue-500 text-white flex items-center justify-center font-medium">
          SC
        </div>
      </div>

      <h2 class="text-sm font-medium text-text-muted mb-3">Pending Forms</h2>

      <div class="space-y-3">
        <div class="p-4 rounded-xl border-2 border-blue-500 bg-blue-50 dark:bg-blue-950/30">
          <div class="flex items-start gap-3">
            <div class="size-10 rounded-lg bg-white dark:bg-surface flex items-center justify-center">
              <IcoFileText class="size-5 text-blue-500" />
            </div>
            <div class="flex-1">
              <h3 class="font-medium text-text">{DEMO_FORM.title}</h3>
              <p class="text-sm text-text-muted">Due Friday, 5:00 PM</p>
            </div>
            <span class="px-2 py-1 rounded-full bg-blue-500 text-white text-xs font-medium">
              New
            </span>
          </div>
        </div>

        <div class="p-4 rounded-xl border border-border bg-surface opacity-60">
          <div class="flex items-start gap-3">
            <div class="size-10 rounded-lg bg-surface-alt flex items-center justify-center">
              <IcoFileText class="size-5 text-text-muted" />
            </div>
            <div class="flex-1">
              <h3 class="font-medium text-text">Monthly Retrospective</h3>
              <p class="text-sm text-text-muted">Due in 5 days</p>
            </div>
          </div>
        </div>
      </div>

      <h2 class="text-sm font-medium text-text-muted mt-6 mb-3">Completed</h2>
      <div class="p-4 rounded-xl border border-border bg-surface opacity-40">
        <div class="flex items-start gap-3">
          <div class="size-10 rounded-lg bg-green-100 dark:bg-green-950 flex items-center justify-center">
            <IcoCheck class="size-5 text-green-600" />
          </div>
          <div class="flex-1">
            <h3 class="font-medium text-text">Last Week's Check-in</h3>
            <p class="text-sm text-text-muted">Submitted Jan 3</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function SubmitFormScreen() {
  const rating = useSignal<number | null>(4);

  return (
    <div class="p-6">
      <div class="flex items-center gap-2 text-sm text-text-muted mb-4">
        <IcoChevronLeft class="size-4" />
        <span>Back</span>
      </div>

      <div class="flex items-start gap-4 mb-6">
        <div class="size-12 rounded-lg bg-surface-alt flex items-center justify-center">
          <IcoFileText class="size-6 text-text-muted" />
        </div>
        <div>
          <h1 class="text-xl font-bold text-text">{DEMO_FORM.title}</h1>
          <p class="text-sm text-text-muted">{DEMO_ORG.name}</p>
        </div>
      </div>

      <div class="space-y-4">
        {/* Question 1 - Rating with selection */}
        <div class="p-4 rounded-xl border border-border bg-surface">
          <div class="flex items-start gap-3 mb-4">
            <span class="size-6 rounded-full bg-surface-muted flex items-center justify-center text-xs font-medium text-text-muted">
              1
            </span>
            <div>
              <p class="font-medium text-text">
                {DEMO_FORM.questions[0]?.title}
              </p>
              <p class="text-sm text-text-muted mt-1">
                {DEMO_FORM.questions[0]?.description}
              </p>
            </div>
          </div>
          <div class="pl-9">
            <RatingInput
              max={5}
              value={rating.value}
              onChange={(v) => {
                rating.value = v;
              }}
            />
          </div>
        </div>

        {/* Question 2 - Text with content */}
        <div class="p-4 rounded-xl border border-border bg-surface">
          <div class="flex items-start gap-3 mb-4">
            <span class="size-6 rounded-full bg-surface-muted flex items-center justify-center text-xs font-medium text-text-muted">
              2
            </span>
            <p class="font-medium text-text">{DEMO_FORM.questions[1]?.title}</p>
          </div>
          <div class="pl-9">
            <div class="rounded-lg border border-border p-3 text-sm text-text">
              Shipped the new dashboard feature ahead of schedule! The team
              collaboration was excellent.
            </div>
          </div>
        </div>

        {/* Question 3 - Empty */}
        <div class="p-4 rounded-xl border border-border bg-surface opacity-60">
          <div class="flex items-start gap-3 mb-4">
            <span class="size-6 rounded-full bg-surface-muted flex items-center justify-center text-xs font-medium text-text-muted">
              3
            </span>
            <div>
              <p class="font-medium text-text">
                {DEMO_FORM.questions[2]?.title}
              </p>
              <p class="text-sm text-text-muted mt-1">
                {DEMO_FORM.questions[2]?.description}
              </p>
            </div>
          </div>
          <div class="pl-9">
            <div class="rounded-lg border border-dashed border-border p-3 text-sm text-text-muted italic">
              Type your answer...
            </div>
          </div>
        </div>
      </div>

      {/* Submit button */}
      <div class="mt-6">
        <button
          type="button"
          class="px-4 py-2 rounded-lg bg-text text-surface text-sm font-medium"
        >
          Submit
        </button>
      </div>
    </div>
  );
}

function ResultsScreen() {
  return (
    <div class="p-6">
      <div class="flex items-center gap-2 text-sm text-text-muted mb-4">
        <IcoChevronLeft class="size-4" />
        <span>Back to Form</span>
      </div>

      <div class="mb-6">
        <h1 class="text-xl font-bold text-text">{DEMO_FORM.title}</h1>
        <p class="text-sm text-text-muted">{DEMO_ORG.name} - Results</p>
      </div>

      <div class="flex items-center justify-between mb-4">
        <div class="flex items-center gap-2">
          <select class="text-sm font-medium border border-border rounded-lg px-3 py-1.5 bg-surface">
            <option>Jan 6 - Jan 12, 2024</option>
            <option>Dec 30 - Jan 5, 2024</option>
          </select>
        </div>
        <span class="text-sm text-text-muted">
          {DEMO_RESULTS.submissionCount} responses
        </span>
      </div>

      <div class="space-y-4">
        {/* Rating question result */}
        <div class="p-4 rounded-xl border border-border bg-surface">
          <div class="flex items-start gap-3 mb-3">
            <div class="size-8 rounded-lg bg-surface-alt flex items-center justify-center">
              <IcoStar class="size-4 text-text-muted" />
            </div>
            <div>
              <p class="font-medium text-text">
                {DEMO_RESULTS.questions[0]?.title}
              </p>
              <p class="text-sm text-text-muted">
                {DEMO_RESULTS.questions[0]?.responseCount} responses
              </p>
            </div>
          </div>
          <div class="pl-11 flex items-center gap-3">
            <div class="flex gap-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <div
                  key={n}
                  class={`size-7 rounded flex items-center justify-center text-xs font-medium ${
                    n <= 3
                      ? "bg-blue-500 text-white"
                      : n === 4
                        ? "bg-blue-300 text-white"
                        : "bg-surface-alt text-text-muted"
                  }`}
                >
                  {n}
                </div>
              ))}
            </div>
            <span class="font-medium text-text">
              {DEMO_RESULTS.questions[0]?.avgRating}
            </span>
          </div>
        </div>

        {/* Text question result */}
        <div class="p-4 rounded-xl border border-border bg-surface">
          <div class="flex items-start gap-3 mb-3">
            <div class="size-8 rounded-lg bg-surface-alt flex items-center justify-center">
              <IcoText class="size-4 text-text-muted" />
            </div>
            <div>
              <p class="font-medium text-text">
                {DEMO_RESULTS.questions[1]?.title}
              </p>
              <p class="text-sm text-text-muted">
                {DEMO_RESULTS.questions[1]?.responses?.length} responses
              </p>
            </div>
          </div>
          <div class="pl-11 space-y-2">
            {DEMO_RESULTS.questions[1]?.responses?.map((r) => (
              <div key={r.name} class="p-3 rounded-lg bg-surface-alt">
                <p class="text-sm text-text">{r.text}</p>
                <p class="text-xs text-text-muted mt-1">{r.name}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
